import { useState } from 'react';
import { IconoAlerta, IconoBasura, IconoCerrar } from '../../componentes/Iconos';
import { useAvisos } from '../../contextos/AvisosContext';
import { useEliminarProyecto } from '../../lib/mutaciones';

interface Props {
  proyecto: {
    id: string;
    codigo: string;
    nombre: string;
  };
  alCerrar: () => void;
  alEliminarExitoso: () => void;
}

export function ModalEliminarProyecto({ proyecto, alCerrar, alEliminarExitoso }: Props) {
  const avisos = useAvisos();
  const eliminarMut = useEliminarProyecto();
  const [confirmacionTexto, setConfirmacionTexto] = useState('');

  const esConfirmado = confirmacionTexto.trim().toUpperCase() === 'ELIMINAR';

  const ejecutarEliminacion = async () => {
    if (!esConfirmado) return;

    try {
      await eliminarMut.mutateAsync(proyecto.id);
      avisos.exito(`Proyecto ${proyecto.codigo} eliminado definitivamente.`);
      alEliminarExitoso();
      alCerrar();
    } catch (err) {
      avisos.error(
        err instanceof Error ? err.message : 'No se pudo eliminar el proyecto.',
      );
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && alCerrar()}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header" style={{ borderBottomColor: 'rgba(211, 47, 47, 0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'var(--accent-red-soft)',
                color: 'var(--accent-red)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconoBasura size={16} />
            </span>
            <h2 style={{ color: 'var(--accent-red)' }}>Eliminar Proyecto Definitivamente</h2>
          </div>
          <button
            type="button"
            className="btn-icono"
            onClick={alCerrar}
            aria-label="Cerrar modal"
          >
            <IconoCerrar size={14} />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>
              {proyecto.nombre}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
              Código: {proyecto.codigo}
            </div>
          </div>

          <div
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--accent-red-soft)',
              color: 'var(--accent-red)',
              fontSize: 12,
              lineHeight: 1.5,
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <IconoAlerta size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>Esta acción es irreversible:</strong> Se eliminarán en cascada segura las cuotas proyectadas, hitos, líneas de presupuesto y notas asociadas.
              <br />
              <span style={{ fontSize: 11, opacity: 0.9, marginTop: 4, display: 'block' }}>
                * Nota de seguridad: Si el proyecto tiene facturas con pagos bancarios ya registrados, el sistema rechazará la eliminación para proteger la contabilidad.
              </span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="inputConfirmarEliminar">
              Para confirmar, escribe <strong style={{ color: 'var(--accent-red)' }}>ELIMINAR</strong> a continuación:
            </label>
            <input
              id="inputConfirmarEliminar"
              type="text"
              value={confirmacionTexto}
              onChange={(e) => setConfirmacionTexto(e.target.value)}
              placeholder="ELIMINAR"
              autoFocus
              style={{
                fontFamily: 'var(--font-mono)',
                borderColor: esConfirmado ? 'var(--accent-red)' : undefined,
              }}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={alCerrar}
            disabled={eliminarMut.isPending}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{
              background: esConfirmado ? 'var(--accent-red)' : undefined,
              borderColor: 'var(--accent-red)',
            }}
            onClick={() => void ejecutarEliminacion()}
            disabled={!esConfirmado || eliminarMut.isPending}
          >
            {eliminarMut.isPending ? 'Eliminando proyecto…' : 'Eliminar Proyecto'}
          </button>
        </div>
      </div>
    </div>
  );
}
